import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { researchosService } from '../../services/researchosService';
import { SortableTh, useSortableRows } from '../../components/tables/SortableTable';

// Chatter → Reddit → Sources (superuser): the platform-owned worklist the Mac mini's cron reads every night.
// Add a subreddit or a search here and the next run backfills it (top all / year / month / week / day + new,
// each to Reddit's ~1,000 ceiling) and then pulls `new` nightly. Pause instead of delete — documents stay.
// House rule: sortable by any column.
const GOLD = 'rgb(var(--brand-gold-rgb))';
const PRIORITIES = ['highest', 'high', 'medium', 'low'];

function fmtDay(s) {
  if (!s) return '—';
  try { return new Date(s).toISOString().slice(0, 10); } catch { return s; }
}
function fmtTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

const COLS = [
  { key: 'name', type: 'string', get: (r) => r.ref },
  { key: 'kind', type: 'string', get: (r) => r.kind },
  { key: 'limit', type: 'number', get: (r) => r.limit },
  { key: 'priority', type: 'string', get: (r) => PRIORITIES.indexOf(r.priority) },
  { key: 'posts', type: 'number', get: (r) => r.posts },
  { key: 'docs', type: 'number', get: (r) => r.docs },
  { key: 'first_post_at', type: 'string', get: (r) => r.first_post_at || '' },
  { key: 'last_post_at', type: 'string', get: (r) => r.last_post_at || '' },
  { key: 'state', type: 'string', get: (r) => (r.needs_backfill ? 'backfill' : r.enabled ? 'live' : 'paused') },
  { key: 'last_checked_at', type: 'string', get: (r) => r.last_checked_at || '' },
];

export default function RedditSourcesPanel() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  // add form
  const [ref, setRef] = useState('');
  const [kind, setKind] = useState('subreddit');
  const [limit, setLimit] = useState('10');
  const [priority, setPriority] = useState('medium');
  const [selftext, setSelftext] = useState(false);
  const [dd, setDd] = useState(false);

  const load = useCallback(async () => {
    try { setRows((await researchosService.getRedditSources()).data || []); setErr(''); }
    catch { setErr('could not load sources'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const data = useMemo(() => rows || [], [rows]);
  const { sorted, toggle, indicator } = useSortableRows(data, COLS);

  const add = async () => {
    const clean = ref.trim();
    if (!clean) { setMsg('A subreddit name or a search query is required.'); return; }
    setBusy(true); setMsg('');
    try {
      const payload = { ref: kind === 'search' ? `search:${clean.replace(/^search:/i, '')}` : clean,
                        limit: Number(limit) || 10, priority, read_selftext: selftext, daily_discussion: dd };
      const r = await researchosService.addRedditSource(payload);
      setMsg(`Tracking ${r.data.ref} — its history sweep runs on the next nightly collection.`);
      setRef(''); await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'could not add the source');
    } finally { setBusy(false); }
  };

  const patch = async (row, fields, note) => {
    setBusy(true); setMsg('');
    try { await researchosService.updateRedditSource(row.id, fields); setMsg(note || ''); await load(); }
    catch (e) { setMsg(e?.response?.data?.error || 'update failed'); }
    finally { setBusy(false); }
  };

  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (rows == null) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>;

  const live = rows.filter((r) => r.enabled).length;
  const pending = rows.filter((r) => r.needs_backfill && r.enabled).length;
  const totPosts = rows.reduce((a, r) => a + (r.posts || 0), 0);

  return (
    <div>
      <div className="mb-5 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
        <div className="mb-3 text-[11px] uppercase tracking-wider text-gray-500">Track a subreddit or a search</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-32">
            <label className="text-xs text-gray-500">Kind</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900">
              <option value="subreddit">Subreddit</option>
              <option value="search">Search</option>
            </select>
          </div>
          <div className="min-w-[220px] flex-1">
            <label className="text-xs text-gray-500">{kind === 'search' ? 'Query' : 'Subreddit'}</label>
            <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder={kind === 'search' ? 'Micron HBM' : 'r/semiconductors'}
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <div className="w-28">
            <label className="text-xs text-gray-500">Nightly limit</label>
            <input type="number" min="1" max="100" value={limit} onChange={(e) => setLimit(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <div className="w-28">
            <label className="text-xs text-gray-500">Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900">
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <button onClick={add} disabled={busy || !ref.trim()}
            className="rounded px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50" style={{ background: GOLD }}>
            {busy ? 'Saving…' : 'Track'}
          </button>
        </div>
        {kind === 'subreddit' && (
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-300">
            <label className="flex items-center gap-2"><input type="checkbox" checked={selftext} onChange={(e) => setSelftext(e.target.checked)} /> keep post bodies (selftext excerpt)</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={dd} onChange={(e) => setDd(e.target.checked)} /> also read the daily-discussion thread&apos;s top comments</label>
          </div>
        )}
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          A new source is swept on the next nightly run: top of all time, year, month, week, day and the newest posts, each to Reddit&apos;s ~1,000 ceiling, merged. From then on the newest posts up to the limit. Nothing is fetched by the server — the collector on the Mac mini reads this list.
        </p>
        {msg && <p className="mt-2 text-xs" style={{ color: GOLD }}>{msg}</p>}
      </div>

      <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        {rows.length} source{rows.length === 1 ? '' : 's'} · {live} live · {pending ? `${pending} awaiting backfill · ` : ''}{totPosts.toLocaleString()} posts stored
      </div>
      {!rows.length ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No sources yet.</p>
      ) : (
        <div className="overflow-x-auto scrollbar-thin rounded-lg border border-gray-200 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-gray-400">
              <tr>
                <SortableTh label="Source" colKey="name" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
                <SortableTh label="Kind" colKey="kind" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
                <SortableTh label="State" colKey="state" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
                <SortableTh label="Limit" colKey="limit" toggle={toggle} indicator={indicator} className="px-3 py-2" />
                <SortableTh label="Priority" colKey="priority" toggle={toggle} indicator={indicator} align="left" className="px-3 py-2" />
                <SortableTh label="Posts" colKey="posts" toggle={toggle} indicator={indicator} className="px-3 py-2" />
                <SortableTh label="Days" colKey="docs" toggle={toggle} indicator={indicator} className="px-3 py-2" />
                <SortableTh label="First" colKey="first_post_at" toggle={toggle} indicator={indicator} className="px-3 py-2" />
                <SortableTh label="Last" colKey="last_post_at" toggle={toggle} indicator={indicator} className="px-3 py-2" />
                <SortableTh label="Collector" colKey="last_checked_at" toggle={toggle} indicator={indicator} className="px-3 py-2" />
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className={`border-t border-gray-100 dark:border-gray-800/60 ${r.enabled ? '' : 'opacity-60'}`}>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                    {r.kind === 'search'
                      ? <span>“{r.name}”</span>
                      : <a href={`https://old.reddit.com/r/${r.name}/`} target="_blank" rel="noreferrer" className="hover:underline">r/{r.name}</a>}
                    {r.daily_discussion && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800">daily thread</span>}
                    {r.read_selftext && <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800">bodies</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{r.kind}</td>
                  <td className="px-3 py-2 text-xs">
                    {!r.enabled
                      ? <span className="text-gray-400">paused</span>
                      : r.needs_backfill
                        ? <span className="text-amber-500" title="full history sweep on the next nightly run">awaiting backfill</span>
                        : <span className="text-green-500" title={r.backfilled_at ? `backfilled ${fmtDay(r.backfilled_at)}` : ''}>live</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                    <input type="number" min="1" max="100" defaultValue={r.limit} disabled={busy}
                      onBlur={(e) => { const n = Number(e.target.value); if (n && n !== r.limit) patch(r, { limit: n }, `${r.ref}: nightly limit ${n}`); }}
                      className="w-16 rounded border border-transparent bg-transparent px-1 text-right hover:border-gray-300 dark:hover:border-gray-700" />
                  </td>
                  <td className="px-3 py-2">
                    <select value={r.priority} disabled={busy} onChange={(e) => patch(r, { priority: e.target.value }, `${r.ref}: priority ${e.target.value}`)}
                      className="rounded border border-transparent bg-transparent px-1 py-0.5 text-xs hover:border-gray-300 dark:bg-gray-900 dark:hover:border-gray-700">
                      {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">{(r.posts || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{r.docs}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtDay(r.first_post_at)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtDay(r.last_post_at)}</td>
                  <td className="px-3 py-2 text-right text-xs text-gray-400" title={r.newest_post_id ? `newest stored post ${r.newest_post_id}` : ''}>{fmtTime(r.last_checked_at)}</td>
                  <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                    <button disabled={busy} onClick={() => patch(r, { enabled: !r.enabled }, `${r.ref}: ${r.enabled ? 'paused' : 'resumed'}`)}
                      className="mr-2 hover:underline" style={{ color: GOLD }}>{r.enabled ? 'Pause' : 'Resume'}</button>
                    {r.enabled && !r.needs_backfill && (
                      <button disabled={busy} onClick={() => patch(r, { needs_backfill: true }, `${r.ref}: history sweep queued for the next run`)}
                        className="hover:underline" style={{ color: GOLD }}>Backfill again</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
